import { TextSelection } from "prosemirror-state";
import type { BlockNoteEditor } from "@blocknote/core";
import { applyCommentMarkToSelection } from "./editorCommentMarks";
import { findQuoteRange } from "./editorCommentQuote";
import { findThreadRange } from "./editorCommentThread";
import { canUseEditorView, getCommentsExtension } from "./editorComments";

const highlightThread = (editor: BlockNoteEditor, threadId?: string) => {
  try {
    getCommentsExtension(editor)?.selectThread(threadId, false);
  } catch {
    return;
  }
};

const scrollThreadIntoView = (
  editor: BlockNoteEditor,
  threadId: string,
  pos: number
) => {
  const view = editor.prosemirrorView;
  if (!view) return;

  const marked = view.dom.querySelector(
    `[data-bn-thread-id="${CSS.escape(threadId)}"]`
  );
  if (marked instanceof HTMLElement) {
    marked.scrollIntoView({ behavior: "smooth", block: "center" });
    return;
  }

  try {
    const coords = view.coordsAtPos(pos);
    const hit = document.elementFromPoint(coords.left, coords.top);
    if (hit instanceof HTMLElement) {
      hit.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  } catch {
    return;
  }
};

export const selectThreadInEditor = (
  editor: BlockNoteEditor,
  threadId: string | undefined,
  quote?: string
) => {
  if (!canUseEditorView(editor) || !threadId) {
    highlightThread(editor, undefined);
    return;
  }

  let range = findThreadRange(editor, threadId, quote);
  if (!range && quote) {
    const found = findQuoteRange(editor, quote, threadId);
    if (found) {
      applyCommentMarkToSelection(editor, threadId, found, {
        stopPending: false,
      });
      range = findThreadRange(editor, threadId, quote);
    }
  }

  if (range) {
    try {
      editor.transact((tr) => {
        tr.setSelection(TextSelection.create(tr.doc, range.from, range.from));
      });
    } catch {
      // Caret can fail if the document changed; still highlight the thread.
    }
    scrollThreadIntoView(editor, threadId, range.from);
  }

  highlightThread(editor, threadId);
};
